import { useState } from "react";
import { toast } from "sonner";
import { Plus, X } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Field, GradButton, fmtMoney } from "@/components/erp";
import { SelectBox } from "@/components/Inputs";
import api, { errMsg } from "@/lib/api";

export const birimMaliyet = (u) => (u?.hammadde_maliyet ? u.cogs || 0 : u?.alis_fiyati || 0);

const Satir = ({ s, i, urunler, hedefId, update, remove }) => {
  const comp = urunler.find((u) => u.id === s.urun_id);
  const tutar = (s.miktar || 0) * (1 + (s.fire_yuzde || 0) / 100) * birimMaliyet(comp);
  return (
    <div className="grid grid-cols-12 gap-2 items-center" data-testid={`recete-satir-${i}`}>
      <div className="col-span-12 sm:col-span-5">
        <SelectBox urun testid={`satir-urun-${i}`} value={s.urun_id} onChange={(v) => update(i, "urun_id", v)}
          options={urunler.filter((u) => u.id !== hedefId).map((u) => ({ value: u.id, label: `${u.kod} · ${u.ad}` }))} />
      </div>
      <Input data-testid={`satir-miktar-${i}`} type="number" step="any" className="col-span-4 sm:col-span-2 bg-white/5" placeholder="Miktar"
        value={s.miktar} onChange={(e) => update(i, "miktar", Number(e.target.value))} />
      <Input data-testid={`satir-fire-${i}`} type="number" step="any" className="col-span-3 sm:col-span-2 bg-white/5" placeholder="Fire %"
        value={s.fire_yuzde} onChange={(e) => update(i, "fire_yuzde", Number(e.target.value))} />
      <div className="col-span-4 sm:col-span-2 text-right font-mono text-xs">{fmtMoney(tutar)}<div className="text-[10px] text-muted-foreground">{comp?.birim}</div></div>
      <button data-testid={`satir-sil-${i}`} onClick={() => remove(i)} className="col-span-1 p-1.5 rounded-lg hover:bg-red-500/10 text-muted-foreground hover:text-red-400"><X className="w-4 h-4" /></button>
    </div>
  );
};

export const ReceteDialog = ({ initial, urunler, ayar, onClose, onSaved }) => {
  const [form, setForm] = useState(initial);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const update = (i, k, v) => setForm((f) => ({ ...f, satirlar: f.satirlar.map((s, n) => (n === i ? { ...s, [k]: v } : s)) }));
  const remove = (i) => setForm((f) => ({ ...f, satirlar: f.satirlar.filter((_, n) => n !== i) }));
  const add = () => setForm((f) => ({ ...f, satirlar: [...f.satirlar, { urun_id: "", miktar: 1, fire_yuzde: 0 }] }));

  const hedef = urunler.find((u) => u.id === form.urun_id);
  const toplam = form.satirlar.reduce((t, s) => t + (s.miktar || 0) * (1 + (s.fire_yuzde || 0) / 100) * birimMaliyet(urunler.find((u) => u.id === s.urun_id)), 0);
  const birim = toplam / (form.cikti_miktari || 1);
  const cogs = birim + (hedef?.operasyon_maliyet || 0);
  const minSatis = cogs * (1 + (ayar?.min_kar_yuzde || 0) / 100);

  const save = async () => {
    if (form.satirlar.some((s) => !s.urun_id)) return toast.error("Tüm satırlarda bileşen seçin");
    try {
      const body = { ...form, satirlar: form.satirlar.map(({ urun_id, miktar, fire_yuzde, aciklama }) => ({ urun_id, miktar, fire_yuzde, aciklama })) };
      if (form.id) await api.put(`/receteler/${form.id}`, body);
      else await api.post("/receteler", body);
      toast.success("Reçete kaydedildi, maliyetler güncellendi");
      onSaved();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="glass max-w-4xl max-h-[90vh] overflow-y-auto erp-scroll" data-testid="recete-dialog">
        <DialogHeader><DialogTitle className="font-head">{form.id ? "Reçeteyi düzenle" : "Yeni reçete"}</DialogTitle></DialogHeader>
        <div className="grid sm:grid-cols-4 gap-4">
          <Field label="Üretilen ürün *" className="sm:col-span-2">
            <SelectBox urun testid="recete-hedef" value={form.urun_id} onChange={(v) => set("urun_id", v)}
              options={urunler.filter((u) => ["mamul", "yari_mamul"].includes(u.kategori_tipi)).map((u) => ({ value: u.id, label: `${u.kod} · ${u.ad}` }))} />
          </Field>
          <Field label={`Çıktı miktarı (${hedef?.birim || "birim"})`}>
            <Input data-testid="recete-cikti" type="number" step="any" className="bg-white/5" value={form.cikti_miktari} onChange={(e) => set("cikti_miktari", Number(e.target.value))} />
          </Field>
          <label className="flex items-end gap-2 text-sm pb-2"><Switch data-testid="recete-aktif" checked={form.aktif} onCheckedChange={(v) => set("aktif", v)} />Aktif reçete</label>
        </div>
        <div className="mt-3 space-y-2">
          <div className="hidden sm:grid grid-cols-12 gap-2 text-[10px] uppercase tracking-wider text-muted-foreground">
            <span className="col-span-5">Bileşen</span><span className="col-span-2">Miktar</span><span className="col-span-2">Fire %</span><span className="col-span-2 text-right">Tutar</span>
          </div>
          {form.satirlar.map((s, i) => <Satir key={i} s={s} i={i} urunler={urunler} hedefId={form.urun_id} update={update} remove={remove} />)}
          <button data-testid="recete-satir-ekle" onClick={add} className="text-sm inline-flex items-center gap-1.5 text-[var(--g1)] hover:brightness-125 mt-1"><Plus className="w-4 h-4" />Bileşen ekle</button>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-4">
          {[["Toplam hammadde", toplam, "recete-toplam"], ["Birim hammadde", birim, "recete-birim"], ["COGS (+operasyon)", cogs, "recete-cogs"], [`Min. satış (%${ayar?.min_kar_yuzde || 0})`, minSatis, "recete-min"]].map(([l, v, t]) => (
            <div key={l} className="rounded-xl bg-white/5 p-3"><div className="text-[10px] uppercase tracking-wider text-muted-foreground">{l}</div><div className="font-mono font-semibold mt-1" data-testid={t}>{fmtMoney(v)}</div></div>
          ))}
        </div>
        <div className="flex justify-end gap-2 mt-4">
          <button onClick={onClose} className="px-5 py-2 rounded-full border border-white/10 text-sm hover:bg-white/5" data-testid="recete-iptal">Vazgeç</button>
          <GradButton onClick={save} data-testid="recete-kaydet">Kaydet</GradButton>
        </div>
      </DialogContent>
    </Dialog>
  );
};
